import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Save, RotateCcw, X, Loader2, Info } from 'lucide-react';

const API = '';

/**
 * Inline editor for per-capability description + prompt_hint overrides.
 *
 * Props:
 *   - capability: { capability_id, name, description, default_description?, is_customized?, prompt_hint? }
 *   - onSaved: () => void   (parent refreshes)
 *   - onClose: () => void
 */
export function CapabilityOverrideEditor({ capability, onSaved, onClose }) {
  const defaultDescription = capability.default_description ?? capability.description;
  const [descOverride, setDescOverride] = useState(
    capability.is_customized ? capability.description : ''
  );
  const [promptHint, setPromptHint] = useState(capability.prompt_hint || '');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/api/ai/capabilities/${encodeURIComponent(capability.capability_id)}/override`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            description_override: descOverride || null,
            prompt_hint: promptHint || null,
          }),
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Save failed');
      } else {
        toast.success('Capability override saved');
        onSaved?.();
        onClose?.();
      }
    } catch (e) { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset to the default description? Your customisations will be removed.')) return;
    setResetting(true);
    try {
      const res = await fetch(
        `${API}/api/ai/capabilities/${encodeURIComponent(capability.capability_id)}/override`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (res.ok) {
        toast.success('Reverted to default');
        onSaved?.();
        onClose?.();
      } else { toast.error('Reset failed'); }
    } catch (e) { toast.error('Reset error'); }
    finally { setResetting(false); }
  };

  return (
    <div
      className="p-3 bg-blue-50/60 dark:bg-blue-950/20 border-t border-blue-200 dark:border-blue-900 space-y-3"
      data-testid={`cap-override-editor-${capability.capability_id}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400 flex items-center gap-1.5">
          <Info className="w-3 h-3" /> Customise how the AI uses this capability
        </span>
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={onClose}>
          <X className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-[10px] font-medium text-muted-foreground">
            Default description (read-only)
          </label>
        </div>
        <div className="text-[11px] p-2 bg-muted/60 rounded border max-h-24 overflow-y-auto whitespace-pre-wrap font-mono">
          {defaultDescription}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-[10px] font-medium text-muted-foreground">
          Description override (optional — leave blank to keep default)
        </label>
        <Textarea
          value={descOverride}
          onChange={(e) => setDescOverride(e.target.value)}
          rows={3}
          placeholder="Custom description shown to the LLM instead of the default. Empty = keep default."
          className="text-xs font-mono"
          data-testid={`cap-override-description-${capability.capability_id}`}
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-[10px] font-medium text-muted-foreground flex items-center gap-1">
          Usage hint (appended to description)
          <span className="text-muted-foreground">— great for nudging the AI toward the right tool choice</span>
        </label>
        <Textarea
          value={promptHint}
          onChange={(e) => setPromptHint(e.target.value)}
          rows={2}
          placeholder='e.g. "Prefer this over orka.web.search when the user asks about their own tasks."'
          className="text-xs"
          data-testid={`cap-override-hint-${capability.capability_id}`}
        />
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm" onClick={save} disabled={saving}
          data-testid={`cap-override-save-${capability.capability_id}`}
        >
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          Save
        </Button>
        {capability.is_customized && (
          <Button
            size="sm" variant="outline" onClick={reset} disabled={resetting}
            data-testid={`cap-override-reset-${capability.capability_id}`}
          >
            {resetting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
            Reset to default
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}
